import { memo, useCallback, useRef, useState } from 'react';

import { CloudUpload } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/shadcn/button';
import { Dialog, DialogContent, DialogTitle } from '@/shadcn/dialog';

import { AvatarComponent } from '@/components/ui/avatar/avatar';
import { TestId } from '@/types/testId';
import { CropImage } from './cropImage/cropImage';

type Props = {
  user?: boolean;
  avatar?: string | null;
  submitNewAvatar: (file: File) => void;
  name?: string;
  loading?: boolean;
};

export const UploadAvatar = memo(
  ({ user = false, avatar = null, submitNewAvatar, name, loading }: Props) => {
    const [srcFile, setSrcFile] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const onAvatarClick = useCallback(() => fileInputRef.current?.click(), []);

    const onFileChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length && e.target.files.length > 0) {
          const targetFile = e.target.files[0];
          const reader = new FileReader();
          reader.onload = () => {
            if (typeof reader.result !== 'string') {
              const errorMessage =
                'Failed to read file, expected string, got ' +
                typeof reader.result;
              toast.error(errorMessage);
              console.error(errorMessage);
              return;
            }
            setSrcFile(reader.result);
          };
          reader.readAsDataURL(targetFile);
        }
      },
      []
    );

    const onCropCancel = useCallback(() => {
      setSrcFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    const onCropSubmit = useCallback(
      cropResult => {
        // cleanup to close the CropDialog
        setSrcFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';

        // submit with the new file
        submitNewAvatar(cropResult.file);
      },
      [submitNewAvatar]
    );

    return (
      <>
        <Button
          aria-label={`Click to modify ${name}'s avatar.`}
          className="group/avatar relative aspect-square size-9 shrink-0 overflow-hidden rounded-full p-0 hover:border-primary"
          data-testid={TestId.UploadAvatar.Button}
          disabled={loading}
          onClick={onAvatarClick}
          variant="outline"
        >
          <AvatarComponent
            className="size-9 transition-all duration-300 group-hover/avatar:blur-xs group-hover/avatar:brightness-90"
            name={name}
            source={avatar}
          />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover/avatar:opacity-100">
            <CloudUpload className="size-4 text-white" />
          </div>
        </Button>
        <input
          aria-hidden="true"
          className="hidden"
          data-testid={TestId.UploadAvatar.Input}
          onChange={onFileChange}
          ref={fileInputRef}
          type="file"
        />
        <Dialog open={!!srcFile}>
          <DialogContent>
            <DialogTitle>Crop the avatar.</DialogTitle>
            <CropImage
              // @ts-expect-error srcFile cannot be null since the dialog open condition is !!srcFile
              actionLabel={user ? 'crop' : 'save'}
              cancel={onCropCancel}
              imageSrc={srcFile}
              submit={onCropSubmit}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }
);
